// The Cierres redesign's "Asistente" page — ported from mantis-business-front-brinks's
// routes/chat.tsx visual design (centered column, empty-state suggestion pills, bubble list,
// sticky composer), wired to the SAME real assistant backend BusinessAssistant.jsx already uses
// (postAssistantMessage / getLatestAssistantConversation / getDocumentSignedUrl / closing
// citations). Brinks' chat.tsx answers every question with a hardcoded respuestaSimulada() —
// there is no equivalent here, on purpose: this page talks to the real Sapiens assistant, so
// suggestion pills and example prompts send a real message instead of playing back canned text.
import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  ArrowUp,
  ArrowUpRight,
  BarChart3,
  Bell,
  Building2,
  File,
  FileSpreadsheet,
  FileText,
  GitCompare,
  Image as ImageIcon,
  Sparkles,
  Square,
} from "lucide-react";

import { selectAuthState } from "../../../store/selectors/authSelectors";
import {
  getLatestAssistantConversation,
  postAssistantMessage,
  regenerateAssistantResponse,
  getDocumentSignedUrl,
  getCierreClosingDetail,
  filterOffices,
} from "../../../ApiCalls/mantisBusiness";
import { googleApiKey } from "../../../config";
import { isClosingCitationLink } from "../../../utils/closingCitations";
import { textoParaEscritura } from "../../../utils/respuestaAsistente";
import { haceCuanto } from "../../../utils/cierreAvisos";
import { errorDelAsistente } from "../../../utils/cierrePermisos";
import { CierreCitadoCard } from "./CierreCitadoCard";
import { CierreDetailDialog } from "./CierreDetailDialog";
import { GraficaAsistente } from "./GraficaAsistente";
import { CopiarRespuesta, RegenerarRespuesta, RespuestaAsistente } from "./RespuestaAsistente";
import { useClienteCierres } from "./ClienteCierresContext";
import "./styles/brinksTheme.css";
import { guia } from "../../../utils/cierreTours";

// Each button shows a short label and sends a whole question that names its period: "hoy" is
// always empty for a client that closes a day late, so they ask about the last closing instead.
// B-167: the backend's refusals of a regeneration, each already a sentence for the person.
const CODIGOS_AL_REGENERAR = ["NOTHING_TO_REGENERATE", "CONVERSATION_CHANGED", "REGENERATE_NOT_AVAILABLE"];

const SUGERENCIAS = [
  { label: "Resumen del último cierre", pregunta: "Dame el resumen del último cierre.", icon: BarChart3 },
  {
    label: "Oficinas con faltante",
    pregunta: "¿Qué oficinas tuvieron faltante en el último cierre y por cuánto?",
    icon: Building2,
  },
  { label: "Alertas pendientes", pregunta: "¿Qué alertas siguen pendientes?", icon: Bell },
  {
    label: "Comparar con el anterior",
    pregunta: "Compara el último cierre con el cierre anterior.",
    icon: GitCompare,
  },
];

const EJEMPLOS = [
  "¿Qué oficinas quedaron descuadradas ayer y por cuánto?",
  "Muéstrame las alertas de esta semana.",
];

// "Hace 5 min" under a reply starts a line of its own, so its first letter is a capital; the bell's
// "hace 5 min" (`haceCuanto`, shared) reads inside a sentence and stays as it is.
const conMayuscula = (texto) => (texto ? texto.charAt(0).toUpperCase() + texto.slice(1) : texto);

const SIN_RESPUESTA =
  "El asistente no generó una respuesta para esta pregunta. Intenta de nuevo o reformúlala.";

// While the assistant works: three dots and a phrase that changes, the way ChatGPT and Claude say
// they are still on it. Cosmetic only — the backend reports no steps.
const FRASES_PENSANDO = [
  "Pensando",
  "Analizando cierres",
  "Revisando oficinas",
  "Consultando alertas",
  "Comparando cifras",
  "Armando la respuesta",
];
const CAMBIO_DE_FRASE_MS = 1800;
const FUNDIDO_MS = 200;

// The reply arrives whole (agent-manager does not stream), so it is written out here: in about
// this many frames whatever its length — ~1,5 s at 60 fps — and never fewer than 2 characters a frame.
const CUADROS_DE_ESCRITURA = 90;

const prefiereSinMovimiento = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

function Pensando() {
  const [indice, setIndice] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    let fundido;
    const intervalo = setInterval(() => {
      setVisible(false);
      fundido = setTimeout(() => {
        setIndice((i) => (i + 1) % FRASES_PENSANDO.length);
        setVisible(true);
      }, FUNDIDO_MS);
    }, CAMBIO_DE_FRASE_MS);
    return () => {
      clearInterval(intervalo);
      clearTimeout(fundido);
    };
  }, []);

  return (
    <div role="status" aria-live="polite" className="flex items-center pt-1 text-sm text-brinks-muted-foreground">
      <span className={`transition-opacity duration-200 ${visible ? "opacity-100" : "opacity-0"}`}>
        {FRASES_PENSANDO[indice]}...
      </span>
    </div>
  );
}

// Writes `texto` out a few characters a frame and hands the part written so far to `children`.
function TextoQueSeEscribe({ texto, onAvance, onFin, children }) {
  const [largo, setLargo] = useState(0);
  const avisos = useRef({ onAvance, onFin });
  avisos.current = { onAvance, onFin };

  useEffect(() => {
    const programar =
      typeof window.requestAnimationFrame === "function"
        ? (fn) => window.requestAnimationFrame(fn)
        : (fn) => setTimeout(fn, 16);
    const cancelar =
      typeof window.cancelAnimationFrame === "function" ? (id) => window.cancelAnimationFrame(id) : clearTimeout;
    const paso = Math.max(2, Math.ceil(texto.length / CUADROS_DE_ESCRITURA));
    let escrito = 0;
    let cuadro;
    const avanzar = () => {
      escrito = Math.min(texto.length, escrito + paso);
      setLargo(escrito);
      avisos.current.onAvance?.();
      if (escrito < texto.length) cuadro = programar(avanzar);
      else avisos.current.onFin?.();
    };
    cuadro = programar(avanzar);
    return () => cancelar(cuadro);
  }, [texto]);

  return children(texto.slice(0, largo));
}

function iconoDeArchivo(filename = "") {
  const f = filename.toLowerCase();
  if (f.includes(".pdf")) return FileText;
  if (f.includes(".doc")) return File;
  if (f.includes(".xls")) return FileSpreadsheet;
  if (f.includes(".jpg") || f.includes(".png") || f.includes(".jpeg")) return ImageIcon;
  return File;
}

function toBlob(base64, type) {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  return new Blob([new Uint8Array(byteNumbers)], { type });
}

// The closings a reply showed (B-125): the backend sends them beside the text as `displays`, one
// `{ type, id }` each, read from the assistant's own show_closings_tool call — never from the text.
// A kind this screen does not draw yet is left out.
const cierresMostrados = (displays) =>
  (Array.isArray(displays) ? displays : []).filter((d) => d?.type === "closing" && d.id).map((d) => d.id);

// B-150: the charts a reply showed, drawn by the backend from its own counts and metrics.
const graficasMostradas = (displays) =>
  (Array.isArray(displays) ? displays : []).filter((d) => d?.type === "chart" && d.chart).map((d) => d.chart);

function CierresChat(props) {
  const [loading, setLoading] = useState(true);
  const [mensajes, setMensajes] = useState([]);
  const [valor, setValor] = useState("");
  const [escribiendo, setEscribiendo] = useState(false);
  // B-167: why the last "Regenerar respuesta" failed, said under the answer it left in place.
  const [falloRegenerar, setFalloRegenerar] = useState(null);
  // Próximamente: la respuesta en voz. Sin `REACT_APP_GOOGLE_API_KEY` en los builds de dev y prod la
  // llamada a Google fallaba en cada respuesta, así que queda silenciada mientras su botón está comentado.
  const [muted] = useState(true);
  const [detalleClosingId, setDetalleClosingId] = useState(null);
  const [filaDetalle, setFilaDetalle] = useState(null);
  const navigate = useNavigate();
  const { elegirCliente, clientCode } = useClienteCierres();
  const [audioUrl, setAudioUrl] = useState(null);
  // "hace 5 min" under each reply moves on by itself: the clock it is read against ticks each minute.
  const [ahora, setAhora] = useState(() => new Date());
  useEffect(() => {
    const reloj = setInterval(() => setAhora(new Date()), 60 * 1000);
    return () => clearInterval(reloj);
  }, []);
  const finRef = useRef(null);
  // B-165: each reply's rendered text, by its place in the list, for "Copiar respuesta".
  const textosRef = useRef({});
  const areaRef = useRef(null);
  // B-169: the question waiting for its reply — `id` tells a reply that arrives after
  // "Detener respuesta" (or after a newer question) from the one being waited for.
  const peticionRef = useRef(null);
  const ultimaPeticion = useRef(0);

  useEffect(() => {
    async function getData() {
      const resp = await getLatestAssistantConversation(
        props.user._id,
        props.mantisBusiness._id,
        props.token
      );
      if (resp.conversation) {
        setMensajes(resp.conversation.responses || []);
      }
      setLoading(false);
    }
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    finRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [mensajes, escribiendo]);

  async function playAudioResponse(message) {
    if (muted) return;
    try {
      const url = `https://texttospeech.googleapis.com/v1/text:synthesize?key=${googleApiKey}`;
      const texto = message.replace(/【.*】/g, "").replace(/\*/g, "").replace(/#/g, "");
      const response = await fetch(url, {
        method: "POST",
        body: JSON.stringify({
          input: { text: texto },
          voice: { languageCode: "es-US", name: "es-US-Standard-A", ssmlGender: "FEMALE" },
          audioConfig: { audioEncoding: "MP3" },
        }),
      });
      const responseJson = await response.json();
      setAudioUrl(URL.createObjectURL(toBlob(responseJson.audioContent, "audio/mpeg")));
    } catch (e) {
      console.log(e);
    }
  }

  const enviar = async (texto) => {
    const limpio = (texto || "").trim();
    if (!limpio || escribiendo) return;
    setFalloRegenerar(null);
    // A reply still being written out is finished at once by a new question.
    setMensajes((prev) => [
      ...prev.map((m) => (m.animar ? { ...m, animar: false } : m)),
      { message: limpio, sender: "User", timestamp: new Date(), type: "text" },
    ]);
    setValor("");
    setEscribiendo(true);
    ultimaPeticion.current += 1;
    const id = ultimaPeticion.current;
    const controller = new AbortController();
    peticionRef.current = { id, controller };
    let response;
    try {
      response = await postAssistantMessage(
        props.user.cellPhone,
        limpio,
        props.token,
        props.user._id,
        props.mantisBusiness._id,
        "deep",
        undefined,
        // The client on the header, so the assistant answers about it without asking which.
        clientCode || undefined,
        controller.signal
      );
    } catch (e) {
      // A timeout or a network failure is said like any other failure below.
      response = { success: false };
    }
    // Stopped, or replaced by a newer question: this reply is no longer awaited. The backend
    // still stores it, so it shows in the history on the next visit.
    if (peticionRef.current?.id !== id) return;
    peticionRef.current = null;
    setEscribiendo(false);
    if (response?.success) {
      // The voice is fetched beside the text, never before it: the reply starts writing at once.
      for (const m of response.messages) {
        if (m.type === "text" && m.sender === "Mantis") {
          playAudioResponse(m.message);
        }
      }
      // postMessage echoes the person's own message first; it is already on screen from the
      // send, so only the assistant's part is added — otherwise every question shows twice.
      // `animar` is this screen's own flag (never stored): the reply is written out, not dropped.
      const animar = !prefiereSinMovimiento();
      setMensajes((prev) => [
        ...prev,
        ...response.messages
          .filter((m) => m.sender !== "User")
          .map((m) => (animar && m.type === "text" && m.message?.trim() ? { ...m, animar: true } : m)),
      ]);
    } else {
      // BR-38 — the assistant's interim rule (BR-36) refuses a person whose access is narrower than
      // the whole business, in a sentence written for them; any other failure keeps this line.
      setMensajes((prev) => [
        ...prev,
        {
          message: errorDelAsistente(response, "Hubo un error al procesar tu mensaje, intenta de nuevo."),
          sender: "Mantis",
          timestamp: new Date(),
          type: "text",
          // Said on screen only, never stored: it is no answer, so it offers no "Regenerar".
          error: true,
        },
      ]);
    }
  };

  // B-167: "Regenerar respuesta" asks the last question again. Its answer leaves the screen while
  // the new one is awaited and the new one takes its place; a failure — or "Detener respuesta" —
  // puts the old one back, since the backend replaces the stored answer only when it has a new one.
  const regenerar = async () => {
    if (escribiendo || peticionRef.current) return;
    const inicio = mensajes.reduce((u, m, i) => (m.sender === "User" ? i : u), -1) + 1;
    const pregunta = inicio > 0 ? mensajes[inicio - 1].message : null;
    if (!pregunta) return;
    const anteriores = mensajes.slice(inicio);
    setFalloRegenerar(null);
    setMensajes((prev) => prev.slice(0, inicio));
    setEscribiendo(true);
    ultimaPeticion.current += 1;
    const id = ultimaPeticion.current;
    const controller = new AbortController();
    peticionRef.current = { id, controller, restaurar: anteriores };
    let response;
    try {
      response = await regenerateAssistantResponse(
        props.token,
        props.user._id,
        props.mantisBusiness._id,
        pregunta,
        clientCode || undefined,
        controller.signal
      );
    } catch (e) {
      response = { success: false };
    }
    if (peticionRef.current?.id !== id) return;
    peticionRef.current = null;
    setEscribiendo(false);
    if (response?.success && response.message) {
      const nueva = response.message;
      if (nueva.type === "text" && nueva.message?.trim()) playAudioResponse(nueva.message);
      const animar = !prefiereSinMovimiento() && nueva.type === "text" && nueva.message?.trim();
      setMensajes((prev) => [...prev, animar ? { ...nueva, animar: true } : nueva]);
    } else {
      setMensajes((prev) => [...prev, ...anteriores]);
      setFalloRegenerar(
        CODIGOS_AL_REGENERAR.includes(response?.errorCode) && response.message
          ? response.message
          : errorDelAsistente(response, "No se pudo regenerar la respuesta. Intenta de nuevo.")
      );
    }
  };

  // B-169: "Detener respuesta" stops waiting — the question stays, marked as stopped, and the
  // composer is free again. The backend cannot be stopped; what it answers later is ignored.
  // B-167: a regeneration stopped puts back the answer it was replacing instead.
  const detener = () => {
    if (!peticionRef.current) return;
    const { restaurar } = peticionRef.current;
    peticionRef.current.controller.abort();
    peticionRef.current = null;
    setEscribiendo(false);
    setMensajes((prev) => {
      if (restaurar) return [...prev, ...restaurar];
      const ultima = prev.reduce((u, m, i) => (m.sender === "User" ? i : u), -1);
      return prev.map((m, i) => (i === ultima ? { ...m, detenida: true } : m));
    });
    setTimeout(() => areaRef.current?.focus(), 0);
  };

  const handleDocumentClick = async (documentId) => {
    const response = await getDocumentSignedUrl(props.token, documentId, props.mantisBusiness._id);
    if (response.success) {
      window.open(response.signedUrl, "_blank");
    }
  };

  // "Ver cierre" from a card hands over the panel row the card already read.
  const verCierre = (closingId, fila = null) => {
    setFilaDetalle(fila);
    setDetalleClosingId(closingId);
  };

  // "Ver día": that day's panel, on the closing's client (the header's selector follows).
  const verDia = ({ businessDate, clientCode }) => {
    if (clientCode) elegirCliente(clientCode);
    navigate(`/business/backoffice/panel-cierres?fecha=${businessDate}`);
  };

  // A closing opened without its row (a citation linked inside a text line): the panel row, asked
  // for its one date and office as the Alertas feed does, so the detail shows its extracted values
  // and offers "Rehacer" as it does from the panel. The date, office and client come from the
  // closing's own detail, since the chat knows only the id.
  useEffect(() => {
    if (!detalleClosingId || filaDetalle) return undefined;
    let activo = true;
    (async () => {
      const mantisBusinessId = props.mantisBusiness._id;
      const detalle = await getCierreClosingDetail(props.token, mantisBusinessId, detalleClosingId).catch(() => null);
      const { businessDate, officeCode, clientCode } = detalle?.success ? detalle.data?.closing || {} : {};
      if (!activo || !businessDate) return;
      const filtros = { startDate: businessDate, endDate: businessDate, office: officeCode || undefined };
      const r = await filterOffices(props.token, filtros, 100, mantisBusinessId, "brinks", clientCode).catch(() => null);
      if (!activo || !r?.success) return;
      setFilaDetalle((r.data || []).find((row) => row.closing_id === detalleClosingId) || null);
    })();
    return () => {
      activo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detalleClosingId]);

  function renderAssistantLink({ href, children, ...anchorProps }) {
    if (!isClosingCitationLink(href)) {
      return (
        <a href={href} {...anchorProps}>
          {children}
        </a>
      );
    }
    return (
      <a
        href={href}
        {...anchorProps}
        onClick={(event) => {
          event.preventDefault();
          // The same detail the card's "Ver cierre" opens, without leaving the chat.
          verCierre(new URLSearchParams(href.split("?")[1]).get("id"));
        }}
      >
        {children}
      </a>
    );
  }

  // B-165. The whole reply is one card, as the question is one bubble: its corner towards the
  // assistant's mark, the text first and, under a hairline, what it showed — charts, closing cards,
  // documents — as part of the same answer rather than loose cards beside it. A card inside it
  // drops its own shadow, so the reply reads as a single surface.
  const BURBUJA_ASISTENTE =
    "rounded-2xl rounded-tl-md border border-brinks-border bg-card px-3 py-3 shadow-brinks-card sm:px-4";
  const ADJUNTOS = "mt-3 space-y-3 border-t border-brinks-border pt-3 [&_section]:shadow-none";

  const renderRespuesta = (texto, contenidoRef) => (
    <RespuestaAsistente texto={texto} renderLink={renderAssistantLink} contenidoRef={contenidoRef} />
  );

  const tieneAdjuntos = (m) =>
    graficasMostradas(m.displays).length > 0 ||
    cierresMostrados(m.displays).length > 0 ||
    (m.relatedDocuments?.length ?? 0) > 0;

  const terminarEscritura = (indice) =>
    setMensajes((prev) => prev.map((m, i) => (i === indice && m.animar ? { ...m, animar: false } : m)));

  // Follows the reply as it grows; "auto" so each frame does not start a new smooth scroll.
  const seguirAlFinal = () => finRef.current?.scrollIntoView({ behavior: "auto", block: "end" });

  // What comes after a reply being written waits for it, so the answers keep their order.
  const enEscritura = mensajes.findIndex((m) => m.animar);

  // The latest reply keeps its actions in sight; earlier ones show them on hover or focus.
  const ultimaRespuesta = mensajes.reduce((ultima, m, i) => (m.sender !== "User" ? i : ultima), -1);

  // B-167: only the conversation's own last answer can be regenerated — not an error said on screen,
  // and not an earlier answer once a question (even a stopped one) follows it.
  const regenerable =
    !escribiendo &&
    ultimaRespuesta === mensajes.length - 1 &&
    mensajes.some((m) => m.sender === "User") &&
    mensajes[ultimaRespuesta]?.type === "text" &&
    !mensajes[ultimaRespuesta]?.error;

  const vacio = !loading && mensajes.length === 0;
  // B-166: the next questions the latest reply offered, once it is written and while nothing is
  // being asked. With the box empty the first one is the box's ghost text — Tab, → or its key
  // writes it in, to send as is or edit first — and the others sit above the composer, each sent
  // with a click. Each question shows once. Typing hides them all.
  const ultima = ultimaRespuesta >= 0 ? mensajes[ultimaRespuesta] : null;
  const ofrecidas =
    ultima && !ultima.animar && !escribiendo && Array.isArray(ultima.suggestions) ? ultima.suggestions : [];
  const [fantasma = null, ...siguientes] = valor ? [] : ofrecidas;
  const completarConFantasma = () => {
    setValor(fantasma);
    requestAnimationFrame(() => {
      const area = areaRef.current;
      if (area) area.setSelectionRange(fantasma.length, fantasma.length);
    });
  };

  return (
    <div className="brinks-scope font-redhat flex min-h-0 flex-1 flex-col bg-background p-3 sm:p-4">
      <div className="mx-auto flex w-full max-w-4xl flex-1 flex-col">
        <div className="flex items-start justify-between pb-1">
          <div>
            <h1 className="font-lora text-[21px] font-semibold text-brinks-foreground">Asistente</h1>
            <p className="text-[13px] text-brinks-muted-foreground">
              Consulta cierres, discrepancias y soportes en lenguaje natural.
            </p>
          </div>
          {/* Próximamente: respuesta en voz (Volume2 / VolumeX de lucide-react; ver `muted` arriba).
          <button
            type="button"
            onClick={() => setMuted((v) => !v)}
            aria-label={muted ? "Activar respuesta en voz" : "Silenciar respuesta en voz"}
            title={muted ? "Activar respuesta en voz" : "Silenciar respuesta en voz"}
            className="grid h-8 w-8 shrink-0 place-items-center rounded-[8px] text-brinks-muted-foreground transition hover:bg-brinks-surface-hover"
          >
            {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
          </button>
          */}
        </div>

        {loading ? (
          <div className="flex flex-1 items-center justify-center text-[13px] text-brinks-muted-foreground">
            Cargando conversación...
          </div>
        ) : vacio ? (
          <div className="flex flex-1 flex-col justify-center pb-6 pt-6">
            <h2 className="text-[24px] font-semibold tracking-tight text-brinks-foreground sm:text-[27px]">
              ¿Qué revisamos hoy?
            </h2>
            <p className="mt-2 text-[14px] text-brinks-muted-foreground">
              Pregunta por cierres, discrepancias o soportes y el asistente arma el resumen por ti.
            </p>
            <div {...guia("Para empezar", "Preguntas frecuentes listas para enviar con un toque.")} className="mt-6 flex flex-wrap gap-2">
              {SUGERENCIAS.map((s) => (
                <button
                  key={s.label}
                  type="button"
                  onClick={() => enviar(s.pregunta)}
                  className="inline-flex items-center gap-2 rounded-full border border-dashed border-brinks-border px-4 py-2 text-[13px] text-brinks-foreground transition hover:border-solid hover:bg-brinks-surface-hover"
                >
                  <s.icon className="h-4 w-4 text-brinks-muted-foreground" />
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex-1 space-y-4 overflow-y-auto py-3">
            {mensajes.map((m, i) =>
              enEscritura >= 0 && i > enEscritura ? null : m.sender === "User" ? (
                <div key={i} className="flex flex-col items-end">
                  <div className="max-w-[85%] whitespace-pre-line rounded-2xl rounded-tr-md bg-primary/80 px-4 py-2.5 text-sm text-primary-foreground">
                    {m.message}
                  </div>
                  {m.detenida ? (
                    <p className="mt-1.5 text-xs text-brinks-muted-foreground">Detuviste la respuesta</p>
                  ) : null}
                </div>
              ) : (
                <div key={i} className="group flex gap-3">
                  <div className="mt-0.5 hidden h-7 w-7 shrink-0 sm:grid place-items-center rounded-full border border-brinks-border bg-card">
                    <Sparkles className="h-3.5 w-3.5 text-brinks-muted-foreground" />
                  </div>
                  <div className="min-w-0 flex-1">
                    {m.type === "text" ? (
                      <div className={BURBUJA_ASISTENTE}>
                        {/* A turn can come back with no text (the agent wrote no reply); an
                            empty bubble reads as the assistant still thinking, so say so. */}
                        {m.message?.trim() ? (
                          m.animar ? (
                            <TextoQueSeEscribe
                              texto={m.message}
                              onAvance={seguirAlFinal}
                              onFin={() => terminarEscritura(i)}
                            >
                              {(escrito) => renderRespuesta(`${textoParaEscritura(escrito)}▍`)}
                            </TextoQueSeEscribe>
                          ) : (
                            renderRespuesta(m.message, (nodo) => {
                              textosRef.current[i] = nodo;
                            })
                          )
                        ) : (
                          <p className="text-sm italic text-brinks-muted-foreground">{SIN_RESPUESTA}</p>
                        )}
                        {!m.animar && tieneAdjuntos(m) ? (
                          <div className={ADJUNTOS}>
                            {/* Its charts, full width, before the cards. */}
                            {graficasMostradas(m.displays).length ? (
                              <div className="grid gap-2">
                                {graficasMostradas(m.displays).map((chart, gi) => (
                                  <GraficaAsistente key={`${chart.title}-${gi}`} chart={chart} />
                                ))}
                              </div>
                            ) : null}
                            {/* The closings the reply talks about, under it once it is written out,
                                two per row from `sm` up. */}
                            {cierresMostrados(m.displays).length ? (
                              <div className="grid gap-2 sm:grid-cols-2">
                                {cierresMostrados(m.displays).map((closingId) => (
                                  <CierreCitadoCard
                                    key={closingId}
                                    closingId={closingId}
                                    token={props.token}
                                    mantisBusinessId={props.mantisBusiness._id}
                                    onVer={verCierre}
                                    onVerDia={verDia}
                                  />
                                ))}
                              </div>
                            ) : null}
                            {m.relatedDocuments?.length ? (
                              <div className="rounded-[12px] border border-brinks-border bg-card p-2.5">
                                <p className="px-1 pb-2 text-[10.5px] font-semibold uppercase tracking-[0.04em] text-brinks-muted-foreground">
                                  {m.relatedDocuments.length} documento
                                  {m.relatedDocuments.length === 1 ? "" : "s"} relacionado
                                  {m.relatedDocuments.length === 1 ? "" : "s"}
                                </p>
                                <div className="grid gap-1.5 sm:grid-cols-2">
                                  {m.relatedDocuments.map((doc, di) => {
                                    const Icono = iconoDeArchivo(doc.filename);
                                    return (
                                      <button
                                        key={di}
                                        type="button"
                                        onClick={() => handleDocumentClick(doc.userDocumentId.toString())}
                                        className="group flex items-center gap-2.5 rounded-[10px] border border-brinks-border bg-background p-2 text-left transition hover:border-brinks-border-strong hover:bg-brinks-surface"
                                      >
                                        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-[8px] bg-brinks-surface text-brinks-muted-foreground">
                                          <Icono className="h-4 w-4" />
                                        </span>
                                        <span className="min-w-0 flex-1 truncate text-[12.5px] font-medium text-brinks-foreground">
                                          {doc.filename}
                                        </span>
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => window.open(m.url)}
                        className="text-sm font-medium text-brinks-foreground underline"
                      >
                        {m.filename}
                      </button>
                    )}
                    {/* Under the reply's card: a round copy button and how long ago it answered
                        (the whole date and time on hover). */}
                    {m.type === "text" && m.message?.trim() && !m.animar ? (
                      <div
                        className={`mt-2 transition-opacity ${
                          i === ultimaRespuesta ? "" : "opacity-0 focus-within:opacity-100 group-hover:opacity-100"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <CopiarRespuesta obtenerNodo={() => textosRef.current[i]} />
                          {regenerable && i === ultimaRespuesta ? (
                            <RegenerarRespuesta onRegenerar={regenerar} />
                          ) : null}
                          {haceCuanto(m.timestamp, ahora) ? (
                            <time
                              dateTime={new Date(m.timestamp).toISOString()}
                              title={new Date(m.timestamp).toLocaleString("es-CO", {
                                dateStyle: "long",
                                timeStyle: "short",
                                timeZone: "America/Bogota",
                              })}
                              className="text-xs text-brinks-muted-foreground"
                            >
                              {conMayuscula(haceCuanto(m.timestamp, ahora))}
                            </time>
                          ) : null}
                        </div>
                        {falloRegenerar && i === ultimaRespuesta ? (
                          <p role="alert" className="mt-1.5 text-xs text-destructive">
                            {falloRegenerar}
                          </p>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </div>
              )
            )}
            {escribiendo ? (
              <div className="flex gap-3">
                <div className="relative mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full border border-brinks-border bg-card">
                  <Sparkles className="h-3.5 w-3.5 text-brinks-muted-foreground" />
                  {/* While it thinks, a ring spins around the assistant's mark. */}
                  <span
                    data-testid="asistente-pensando"
                    aria-hidden="true"
                    className="absolute -inset-px animate-spin rounded-full border-2 border-transparent border-t-brinks-foreground motion-reduce:animate-none"
                  />
                </div>
                <Pensando />
              </div>
            ) : null}
            <div ref={finRef} className="!mt-0" />
          </div>
        )}

        {/* The reply fades under the composer instead of being cut off by it. */}
        <div className="sticky bottom-0 mt-2 bg-background pb-2 before:pointer-events-none before:absolute before:inset-x-0 before:-top-6 before:h-6 before:bg-gradient-to-t before:from-background before:to-transparent before:content-['']">
          {siguientes.length ? (
            <div
              {...guia("Otras preguntas", "Otras preguntas para seguir con lo que acaba de responder. Toca una para enviarla.")}
              role="group"
              aria-label="Otras preguntas sugeridas"
              className="relative mb-2.5 animate-in fade-in slide-in-from-bottom-1 duration-300 motion-reduce:animate-none"
            >
              <div className="relative min-w-0">
                <div className="flex gap-1.5 overflow-x-auto pr-8 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                  {siguientes.map((pregunta) => (
                    <button
                      key={pregunta}
                      type="button"
                      onClick={() => enviar(pregunta)}
                      title={`Enviar: ${pregunta}`}
                      className="group/sugerencia inline-flex h-8 max-w-[22rem] shrink-0 items-center gap-1.5 rounded-full border border-transparent bg-brinks-surface px-3 text-[13px] text-brinks-foreground/80 transition hover:border-brinks-border hover:bg-card hover:text-brinks-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brinks-ring"
                    >
                      <span className="truncate">{pregunta}</span>
                      <ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-brinks-muted-foreground/60 transition group-hover/sugerencia:text-brinks-foreground" />
                    </button>
                  ))}
                </div>
                {/* When they do not fit, the last one fades out at the edge instead of being cut. */}
                <div className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-background to-transparent" />
              </div>
            </div>
          ) : null}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              enviar(valor);
            }}
            {...guia("Tu pregunta", "Escribe aquí y presiona Enter. Los cierres y las gráficas que te muestre se pueden abrir.")} className="rounded-[12px] border border-brinks-border bg-card shadow-brinks-card transition focus-within:border-brinks-ring"
          >
            <div className="flex items-start gap-2 px-4 pt-4">
              <Sparkles className="mt-1 h-4 w-4 shrink-0 text-brinks-muted-foreground" />
              <div className="relative w-full">
              <textarea
                ref={areaRef}
                rows={2}
                value={valor}
                onChange={(e) => setValor(e.target.value)}
                onKeyDown={(e) => {
                  if (fantasma && ((e.key === "Tab" && !e.shiftKey) || e.key === "ArrowRight")) {
                    e.preventDefault();
                    completarConFantasma();
                    return;
                  }
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    enviar(valor);
                  }
                }}
                placeholder={fantasma ? "" : "Pregunta por una oficina, una fecha o una diferencia..."}
                aria-describedby={fantasma ? "pregunta-sugerida" : undefined}
                disabled={escribiendo}
                className="min-h-16 w-full resize-none bg-transparent text-sm text-brinks-foreground outline-none placeholder:text-brinks-muted-foreground"
              />
              {/* B-166: the suggested question as ghost text, ending in the key that writes it in —
                  pressed (Tab or →) or clicked. Only the key takes the pointer; the rest is the box. */}
              {fantasma ? (
                <div className="pointer-events-none absolute inset-x-0 top-0 text-sm text-brinks-muted-foreground">
                  <span id="pregunta-sugerida">{fantasma}</span>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      completarConFantasma();
                      areaRef.current?.focus();
                    }}
                    title="Completar con esta pregunta (Tab o →)"
                    aria-label={`Completar con: ${fantasma}`}
                    className="pointer-events-auto ml-2 inline-flex translate-y-[-1px] items-center gap-1 rounded-md border border-brinks-border bg-card px-1.5 py-0.5 align-middle text-[11px] font-medium leading-none text-brinks-foreground shadow-sm transition hover:border-brinks-ring hover:bg-brinks-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brinks-ring"
                  >
                    Tab
                    <svg aria-hidden="true" viewBox="0 0 16 16" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M2 8h10M9 4.5 12.5 8 9 11.5M14 4v8" />
                    </svg>
                  </button>
                </div>
              ) : null}
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 px-4 pb-2">
              {/* B-169: while the assistant thinks, the same button stops it. */}
              {escribiendo ? (
                <button
                  type="button"
                  onClick={detener}
                  aria-label="Detener respuesta"
                  title="Detener respuesta"
                  className="relative grid h-9 w-9 place-items-center rounded-full bg-primary text-primary-foreground transition hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brinks-ring"
                >
                  <Square className="h-3 w-3 fill-current" aria-hidden="true" />
                  {/* The same ring that spins around the assistant's mark while it thinks. */}
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute -inset-[3px] animate-spin rounded-full border-2 border-transparent border-t-brinks-foreground motion-reduce:animate-none"
                  />
                </button>
              ) : (
                <button
                  type="submit"
                  aria-label="Enviar"
                  disabled={!valor.trim()}
                  className="grid h-9 w-9 place-items-center rounded-full bg-primary text-primary-foreground transition hover:opacity-90 disabled:opacity-40"
                >
                  <ArrowUp className="h-4 w-4" />
                </button>
              )}
            </div>

            <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 border-t border-brinks-border px-3 py-2.5 sm:flex sm:justify-between">
              <span className="inline-flex min-w-0 items-center gap-2 rounded-full border border-brinks-border px-3 py-1.5 text-xs font-medium text-brinks-foreground">
                <span className="grid h-4 w-4 place-items-center rounded-full bg-primary text-[9px] font-bold text-primary-foreground">
                  M
                </span>
                <span className="truncate">Asistente Brinks</span>
              </span>
              {/* Próximamente: adjuntar archivos a la pregunta (Paperclip de lucide-react).
              <div className="flex shrink-0 items-center gap-2">
                <button
                  type="button"
                  disabled
                  title="Adjuntar (próximamente)"
                  className="inline-flex items-center gap-2 rounded-full border border-brinks-border px-3 py-1.5 text-xs text-brinks-muted-foreground/50"
                >
                  <Paperclip className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">Adjuntar</span>
                </button>
              </div>
              */}
            </div>
          </form>

          {vacio ? (
            <div className="mt-3 rounded-xl border border-brinks-border bg-brinks-surface px-4 py-3">
              <p className="text-xs font-medium text-brinks-muted-foreground">Prueba con:</p>
              <div className="mt-2 flex flex-col gap-1.5">
                {EJEMPLOS.map((e) => (
                  <button
                    key={e}
                    type="button"
                    onClick={() => enviar(e)}
                    className="truncate text-left text-sm text-brinks-foreground/80 transition hover:text-brinks-foreground"
                  >
                    {e}
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <CierreDetailDialog
        closingId={detalleClosingId}
        panelRow={filaDetalle}
        token={props.token}
        mantisBusinessId={props.mantisBusiness._id}
        onOpenChange={(open) => !open && verCierre(null)}
      />

      {audioUrl ? <audio src={audioUrl} autoPlay className="hidden" /> : null}
    </div>
  );
}

export default connect(selectAuthState)(CierresChat);
